import View                    from '@widesk-ui/components/View';
import Popover                 from '@widesk-ui/components/Popover';
import { observer }            from 'mobx-react-lite';
import React                   from 'react';
import { DownOutlined }        from '@ant-design/icons';
import { ScissorOutlined }     from '@ant-design/icons';
import { ToolOutlined }        from '@ant-design/icons';
import { CloudUploadOutlined } from '@ant-design/icons';
import currentEditorStore      from '@/TokamapEditor/stores/currentEditorStore';
import buildingPickStore       from '@/TokamapEditor/stores/buildingPickStore';
import leftSidebarStore        from '@/TokamapEditor/stores/leftSidebarStore';
import planImportStore         from '@/TokamapEditor/planImport/planImportStore';
import toggleOpeningTool       from '@/TokamapEditor/helpers/toggleOpeningTool';
import toggleCutTool           from '@/TokamapEditor/helpers/toggleCutTool';
import MagicWandOutlined       from '@/TokamapEditor/ux/icons/MagicWandOutlined';
import DoorOpenOutlined        from '@/TokamapEditor/ux/icons/DoorOpenOutlined';
import Kbd                     from '@/TokamapEditor/ux/Kbd';
import { SHOW_PLAN_IMPORT }    from '@/TokamapEditor/ux/constants';
import ToolPreview             from '@/TokamapEditor/ux/ToolPreview';
import type { ToolPreviewName } from '@/TokamapEditor/ux/ToolPreview';
import useDesignTokens         from '@/hooks/useDesignTokens';

const ToolsButton = observer(() => {
	const tokens = useDesignTokens();
	const editor = currentEditorStore.get;

	const [opened, setOpened] = React.useState(false);
	const [opening, setOpening] = React.useState(false);
	const [cutting, setCutting] = React.useState(false);

	React.useEffect(() => {
		if (!editor) return;

		return editor.mapAdapter.on(event => {
			if (event.type === 'opening:tool-changed') return setOpening(event.active);
			if (event.type === 'cut:tool-changed') return setCutting(event.active);
		});
	}, [editor]);

	const importing = planImportStore.modalOpened || leftSidebarStore.options.reference === 'plan-placement';
	const canPick = opened && buildingPickStore.isAvailable() && buildingPickStore.canPick();
	const showsPick = opened && buildingPickStore.isAvailable();
	const active = opened || opening || cutting || buildingPickStore.active || importing;

	const pick = (action: () => void) => () => {
		setOpened(false);
		action();
	};

	return (
		<Popover
			trigger="click"
			placement="bottomRight"
			autoAdjustOverflow={{ shiftX: true }}
			open={opened}
			onOpenChange={setOpened}
			styles={{ body: { padding: 0 } }}
			content={(
				<View p="xs" gap={2} style={{ width: 260 }}>
					<SectionTitle>Modifier une forme</SectionTitle>

					<Entry
						icon={<DoorOpenOutlined style={{ fontSize: 16 }} />}
						preview="opening"
						label="Ouverture"
						hint="Glisser le long d'un mur"
						shortCut="O"
						selected={opening}
						onClick={pick(() => toggleOpeningTool(editor))}
					/>

					<Entry
						icon={<ScissorOutlined style={{ fontSize: 16 }} />}
						preview="cut"
						label="Découpe"
						hint="Diviser une forme en plusieurs morceaux"
						shortCut="X"
						selected={cutting}
						onClick={pick(() => toggleCutTool(editor))}
					/>

					{(showsPick || SHOW_PLAN_IMPORT) && (
						<>
							<View h={1} bg={tokens.border.subtle} marginV={4} />

							<SectionTitle>Générer des formes</SectionTitle>
						</>
					)}

					{showsPick && (
						<Entry
							icon={<MagicWandOutlined style={{ fontSize: 16 }} />}
							preview="buildingPick"
							label="Baguette magique"
							hint={canPick ? 'Cliquez sur un bâtiment de la carte pour le reprendre' : 'Zoomez pour voir les bâtiments'}
							shortCut="W"
							selected={buildingPickStore.active}
							disabled={!canPick}
							onClick={pick(() => buildingPickStore.toggle())}
						/>
					)}

					{SHOW_PLAN_IMPORT && (
						<Entry
							icon={<CloudUploadOutlined style={{ fontSize: 16 }} />}
							preview="planImport"
							label="Importer un plan"
							hint="DXF, SVG, PDF ou image"
							selected={importing}
							disabled={importing}
							onClick={pick(() => planImportStore.open())}
						/>
					)}
				</View>
			)}
		>
			<View
				row
				centerV
				gap="xs"
				px="sm"
				h="100%"
				pointer
				color={tokens.text.default}
				bg={active ? tokens.surface.subtle : undefined}
				hover={{ bg: tokens.surface.subtle }}
			>
				<ToolOutlined style={{ fontSize: 16 }} />

				<View bold>Outils</View>

				<DownOutlined
					style={{ fontSize: 10, opacity: 0.55, transition: 'transform .2s ease' }}
					rotate={opened ? 180 : 0}
				/>
			</View>
		</Popover>
	);
});

const SectionTitle = (props: { children: React.ReactNode; }) => {
	const tokens = useDesignTokens();

	return (
		<View px="sm" pt={4} size={11.5} bold color={tokens.text.muted}>
			{props.children}
		</View>
	);
};

const Entry = (props: {
	icon: React.ReactNode;
	preview?: ToolPreviewName;
	label: string;
	hint: string;
	shortCut?: string;
	selected: boolean;
	disabled?: boolean;
	onClick: () => void;
}) => {
	const tokens = useDesignTokens();

	const entry = (
		<View
			row
			centerV
			gap="sm"
			px="sm"
			py="xs"
			br={8}
			pointer={!props.disabled}
			color={props.disabled ? tokens.text.muted : tokens.text.default}
			bg={props.selected ? tokens.fill.subtle : undefined}
			hover={props.disabled ? undefined : { bg: tokens.surface.subtle }}
			onClick={props.disabled ? undefined : props.onClick}
		>
			{props.icon}

			<View flex>
				<View>{props.label}</View>
				<View size={11.5} color={props.disabled ? tokens.text.muted : tokens.text.secondary}>{props.hint}</View>
			</View>

			{props.shortCut && <Kbd>{props.shortCut}</Kbd>}
		</View>
	);

	return props.preview
		? <ToolPreview name={props.preview} label={props.label} shortCut={props.shortCut}>{entry}</ToolPreview>
		: entry;
};

export default ToolsButton;
